import React, { useEffect } from 'react';
import TextField, { TextFieldPropsSizeOverrides } from '@mui/material/TextField';
import CircularProgress from "@mui/material/CircularProgress";
import { Controller, UseControllerProps } from 'react-hook-form';
import { Typography, InputAdornment, SxProps, Theme, Box } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { IMaskInput } from 'react-imask';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

interface ControlledTextFieldBaseProps {
  label: any;
  id: string;
  name: string;
  className?: string;
  variant?: "standard" | "filled" | "outlined";
  disabled?: boolean;
  control: any;
  defaultValue?: any;
  rules?: UseControllerProps['rules'];
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTextChange?: (event: any) => void;
  OnPaste?: any;
  required?: boolean;
  setValue?: any;
  register?: any;
  maxLength?: number;
  minLength?: number;
  labelType?: "Material" | "Normal";
  description?: IDescription;
  suffix?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef?: any;
  size?: OverridableStringUnion<'small' | 'medium', TextFieldPropsSizeOverrides>;
  mask?: string;
  formatProps?: NumericFormatProps;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
  sendOtpAction?: (phone: string) => void;
  sendOtpText?: string;
  showPhonePrefix?: boolean;
  getValues?: () => Record<string, any>;
  isVerified?: boolean;
  isOtpLoading?: boolean;
}

interface IDescription {
  text: string | any;
  align: "Top" | "Bottom";
  style?: React.CSSProperties;
  className?: string;
}

export enum TextType {
  Text = "text",
  Password = "password",
  Number = "number",
  Email = "email",
  Date = "date",
  Currency = "currency",
  Otp = "Otp",
  AllowOnlyNumber = "AllowOnlyNumber"
}

export enum CurrencyType {
  Rupee = "₹",
  Dollar = "$"
}

interface CurrencyFieldProps extends ControlledTextFieldBaseProps {
  type: TextType.Currency;
  currency: CurrencyType;
}

interface NonCurrencyFieldProps extends ControlledTextFieldBaseProps {
  type?: Exclude<TextType, TextType.Currency>;
  currency?: never;
}

type ControlledTextFieldProps = CurrencyFieldProps | NonCurrencyFieldProps;

const TextMaskCustom = React.forwardRef<HTMLInputElement, { onChange: (event: { target: { name: string; value: string } }) => void; name: string; mask: string; }>(
  function TextMaskCustom(props, ref) {
    const { onChange, mask, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask={mask}
        inputRef={ref}
        onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
        overwrite
      />
    );
  }
);

const NumericFormatCustom = React.forwardRef<NumericFormatProps, { onChange: (event: { target: { name: string; value: string } }) => void; name: string; formatProps?: NumericFormatProps; }>(
  function NumericFormatCustom(props, ref) {
    const { onChange, formatProps, ...other } = props;
    return (
      <NumericFormat
        {...formatProps}
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
      />
    );
  }
);

const WSTextBoxWithOtp: React.FC<ControlledTextFieldProps> = ({
  id,
  label,
  className,
  control,
  name,
  rules,
  variant = "outlined",
  disabled = false,
  defaultValue = '',
  setValue,
  onChange,
  onTextChange = () => { },
  OnPaste = () => { },
  type = TextType.Text,
  maxLength,
  minLength,
  labelType = "Material",
  description,
  currency,
  suffix,
  onKeyDown,
  inputRef,
  size = "small",
  mask,
  formatProps,
  autoFocus = false,
  sx,
  sendOtpAction,
  sendOtpText = "Send OTP",
  showPhonePrefix = false,
  getValues,
  isVerified,
  isOtpLoading = false,
}) => {
  useEffect(() => {
    if (setValue && defaultValue !== undefined) {
      setValue(name, defaultValue);
    }
  }, [defaultValue, setValue]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => (
        <>
          {description && description.align === "Top" && (
            <Typography style={description.style}>{description.text}</Typography>
          )}
          {labelType === 'Normal' && (
            <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.75rem' }} htmlFor={id}>
              {label}
            </label>
          )}
          <TextField
            size={size}
            id={id}
            autoFocus={autoFocus}
            fullWidth
            {...field}
            className={fieldState.invalid ? 'WSError' : className}
            label={labelType === 'Material' ? label : undefined}
            variant={variant}
            disabled={disabled}
            style={{ backgroundColor: disabled ? 'transparent' : '#fff' }}
            onKeyDown={onKeyDown}
            type={type !== TextType.Currency ? type : 'text'}
            error={fieldState.invalid}
            value={field.value}
            onPaste={OnPaste}
            inputRef={inputRef}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.75rem',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.75rem',
              },
              ...sx
            }}
            helperText={fieldState.error ? fieldState.error.message : ''}
            inputProps={{
              min: 0,
              maxLength,
              minLength,
              inputMode: type === TextType.Currency || type === TextType.Number || type === TextType.AllowOnlyNumber ? 'numeric' : 'text',
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              let value = e.target.value;
              if (type === TextType.AllowOnlyNumber) {
                value = value.replace(/\D/g, "");
              }
              field.onChange({ target: { name, value } });
              onTextChange({ target: { name, value } });
            }}
            InputProps={{
              startAdornment: showPhonePrefix ? (
                <InputAdornment position="start">
                  <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    +91 -
                  </Box>
                </InputAdornment>
              ) : (
                type === TextType.Currency && currency && (
                  <InputAdornment position="start">{currency}</InputAdornment>
                )
              ),
              endAdornment: (
                <>
                  {suffix && <InputAdornment position="end" className="suffixAdornment">{suffix}</InputAdornment>}
                  {sendOtpAction && (
                    <InputAdornment position="end">
                      {isVerified ? (
                        <Box
                          sx={{
                            color: "green",
                            fontSize: "1rem",
                            fontWeight: "bold",
                          }}
                        >
                          ✓
                        </Box>
                      ) : isOtpLoading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Typography
                          onClick={() => {
                            const phone = getValues?.()[name];

                            if (phone && !isOtpLoading) {
                              sendOtpAction?.(phone);
                            }
                          }}
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {sendOtpText}
                        </Typography>
                      )}
                    </InputAdornment>
                  )}
                </>
              ),
              inputComponent: mask
                ? TextMaskCustom as any
                : type === TextType.Currency
                  ? NumericFormatCustom as any
                  : undefined,
            }}
          />
          {description && description.align === "Bottom" && (
            <Typography style={description.style} className={description.className}>
              {description.text}
            </Typography>
          )}
        </>
      )}
    />
  );
};

export default WSTextBoxWithOtp;
